import { type LinkAttributes, LinkNode, type SerializedLinkNode } from '@lexical/link';
import { isHTMLAnchorElement } from '@lexical/utils';
import type { DOMConversionMap, DOMConversionOutput, NodeKey, EditorConfig, LexicalEditor} from 'lexical';
import { $applyNodeReplacement, $getSelection, $isElementNode, $isRangeSelection, ElementNode, type LexicalNode } from 'lexical';

export type SerializedCustomLinkNode = SerializedLinkNode & {
	classes: string;
};

export class CustomLinkNode extends LinkNode {
	__classes: string;

	static getType(): string {
		return "custom-link";
	}

	static clone(node: CustomLinkNode): CustomLinkNode {
		return new CustomLinkNode(
			node.__url,
			{rel: node.__rel, target: node.__target, title: node.__title},
			node.__classes,
			node.__key
		);
	}

	constructor(url: string, attributes: LinkAttributes = {}, classes: string, key?: NodeKey) {
		//console.log(url, attributes, classes, key);
		super(url, attributes, key);
		this.__classes = classes;
	}

	getURL(): string {
		return this.getLatest().__url;
	}

	getClasses(): string {
		return this.__classes;
	}

	setClasses(classes: string) {
		const writable = this.getWritable();
		writable.__classes = classes;
	}

	createDOM(config: EditorConfig) {
		const dom = super.createDOM(config);
		dom.className = this.getClasses();
		return dom;
	}

	exportDOM(editor: LexicalEditor) {
		return super.exportDOM(editor);
	}

	updateDOM(prevNode: CustomLinkNode, dom: HTMLElement) {
		const classes = this.__classes;

		if (classes !== prevNode.__classes) {
			dom.className = classes;
		}

		return false;
	}

	static importDOM(): DOMConversionMap {
		return {
			a: (node: Node) => ({
				conversion: convertAnchorElement,
				priority: 1,
			}),
		};
	}

	static importJSON(serializedNode: SerializedCustomLinkNode): LinkNode {
		const node = $createCustomLinkNode(serializedNode.url, {
			rel: serializedNode.rel,
			target: serializedNode.target,
			title: serializedNode.title,
		}).updateFromJSON(serializedNode);
		node.setClasses(serializedNode.classes);
		return node;
	}

	exportJSON(): SerializedCustomLinkNode {
		return {
			...super.exportJSON(),
			type: 'custom-link',
			classes: this.getClasses(),
		};
	}

}

function convertAnchorElement(domNode: HTMLElement): DOMConversionOutput {
	let node = null;
	if (isHTMLAnchorElement(domNode)) {
		const content = domNode.textContent;
		if ((content !== null && content !== '') || domNode.children.length > 0) {
			node = $createCustomLinkNode(domNode.getAttribute('href') || '', {
				rel: domNode.getAttribute('rel'),
				target: domNode.getAttribute('target'),
				title: domNode.getAttribute('title'),
			});
		}
	}

	if (node && domNode.classList.length) {
		node.setClasses(domNode.classList.value);
	}

	return {node};
}

export function $isCustomLinkNode(node: LexicalNode|null|undefined): node is CustomLinkNode {
	return node instanceof CustomLinkNode;
}

export function $createCustomLinkNode(url: string, attributes?: LinkAttributes, classes = ''): CustomLinkNode {
	//console.log(url, attributes, classes);
	return $applyNodeReplacement(new CustomLinkNode(url, attributes, classes));
}

export function toggleLink(
	url: null | string,
	attributes: LinkAttributes = {},
): void {
	const {target, title} = attributes;
	const rel = attributes.rel === undefined ? 'noreferrer' : attributes.rel;
	const selection = $getSelection();

	if (!$isRangeSelection(selection)) {
		return;
	}
	const nodes = selection.extract();

	if (url === null) {
		// Remove LinkNodes
		nodes.forEach((node) => {
			const parent = node.getParent();

			if ($isCustomLinkNode(parent)) {
				const children = parent.getChildren();

				for (let i = 0; i < children.length; i++) {
					parent.insertBefore(children[i]);
				}

				parent.remove();
			}
		});
	} else {
		// Add or merge LinkNodes
		if (nodes.length === 1) {
			const firstNode = nodes[0];
			// if the first node is a LinkNode or if its
			// parent is a LinkNode, we update the URL, target and rel.
			const linkNode: CustomLinkNode = $isCustomLinkNode(firstNode)
				? firstNode
				: $getLinkAncestor(firstNode) as CustomLinkNode;
			if (linkNode !== null) {
				linkNode.setURL(url);
				if (target !== undefined) {
					linkNode.setTarget(target);
				}
				if (rel !== null) {
					linkNode.setRel(rel);
				}
				if (title !== undefined) {
					linkNode.setTitle(title);
				}
				return;
			}
		}

		let prevParent: ElementNode | LinkNode | null = null;
		let linkNode: LinkNode | null = null;

		nodes.forEach((node) => {
			const parent = node.getParent();

			if (
				parent === linkNode ||
				parent === null ||
				($isElementNode(node) && !node.isInline())
			) {
				return;
			}

			if ($isCustomLinkNode(parent)) {
				linkNode = parent;
				parent.setURL(url);
				if (target !== undefined) {
					parent.setTarget(target);
				}
				if (rel !== null) {
					linkNode.setRel(rel);
				}
				if (title !== undefined) {
					linkNode.setTitle(title);
				}
				return;
			}

			if (!parent.is(prevParent)) {
				prevParent = parent;
				linkNode = $createCustomLinkNode(url, {rel, target});

				if ($isCustomLinkNode(parent)) {
					if (node.getPreviousSibling() === null) {
						parent.insertBefore(linkNode);
					} else {
						parent.insertAfter(linkNode);
					}
				} else {
					node.insertBefore(linkNode);
				}
			}

			if ($isCustomLinkNode(node)) {
				if (node.is(linkNode)) {
					return;
				}
				if (linkNode !== null) {
					const children = node.getChildren();

					for (let i = 0; i < children.length; i++) {
						linkNode.append(children[i]);
					}
				}

				node.remove();
				return;
			}

			if (linkNode !== null) {
				linkNode.append(node);
			}
		});
	}
}

function $getLinkAncestor(node: LexicalNode): null | LexicalNode {
	return $getAncestor(node, $isCustomLinkNode);
}

function $getAncestor<NodeType extends LexicalNode = LexicalNode>(
	node: LexicalNode,
	predicate: (ancestor: LexicalNode) => ancestor is NodeType,
): null | LexicalNode {
	let parent: null | LexicalNode = node;
	while (parent !== null && (parent = parent.getParent()) !== null && !predicate(parent)) {

	}
	return parent;
}
